import type { RelayConfig } from '../types/identity';
import type { MsgChiffre, MsgSignature, MsgCle } from '../types/message';

/** Timeout for relay fetch (GET/POST). X_RELAY_TIMEOUT when exceeded. */
export const RELAY_FETCH_TIMEOUT_MS = 15000;

/** Maximum number of relays that can be configured. */
export const MAX_RELAYS = 8;

/**
 * Default relay endpoint.
 */
const DEFAULT_RELAY_ENDPOINT = 'https://relay.certificator.4nkweb.com';

/**
 * Get stored relay configurations.
 * Initializes with default relay if none are configured.
 */
export function getStoredRelays(): RelayConfig[] {
  try {
    const stored = localStorage.getItem('userwallet_relays');
    if (stored === null) {
      // Initialize with default relay
      const defaultRelays: RelayConfig[] = [
        {
          endpoint: DEFAULT_RELAY_ENDPOINT,
          priority: 0,
          enabled: true,
        },
      ];
      storeRelays(defaultRelays);
      return defaultRelays;
    }
    const raw = JSON.parse(stored) as RelayConfig[];
    // If empty, initialize with default relay
    if (raw.length === 0) {
      const defaultRelays: RelayConfig[] = [
        {
          endpoint: DEFAULT_RELAY_ENDPOINT,
          priority: 0,
          enabled: true,
        },
      ];
      storeRelays(defaultRelays);
      return defaultRelays;
    }
    const relays = raw.slice(0, MAX_RELAYS);
    if (raw.length > MAX_RELAYS) {
      storeRelays(relays);
    }
    return relays;
  } catch (error) {
    console.error('Error reading stored relays:', error);
    // Initialize with default relay on error
    const defaultRelays: RelayConfig[] = [
      {
        endpoint: DEFAULT_RELAY_ENDPOINT,
        priority: 0,
        enabled: true,
      },
    ];
    storeRelays(defaultRelays);
    return defaultRelays;
  }
}

/**
 * Store relay configurations.
 */
export function storeRelays(relays: RelayConfig[]): void {
  localStorage.setItem('userwallet_relays', JSON.stringify(relays));
}

/**
 * Test relay connectivity.
 */
export async function testRelay(endpoint: string): Promise<boolean> {
  try {
    const response = await fetch(`${endpoint}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * GET encrypted messages from relay.
 * Throws on fetch failure or non-ok response.
 */
export async function getMessagesChiffres(
  relay: string,
  windowStart: number,
  windowEnd: number,
  serviceUuid?: string,
): Promise<MsgChiffre[]> {
  const params = new URLSearchParams({
    start: windowStart.toString(),
    end: windowEnd.toString(),
  });
  if (serviceUuid !== undefined) {
    params.append('service', serviceUuid);
  }
  const response = await fetch(`${relay}/messages?${params.toString()}`, {
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /messages failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as MsgChiffre[];
}

/**
 * GET signatures for a message hash.
 * Throws on fetch failure or non-ok response.
 * @param timeoutMs - Optional. Default RELAY_FETCH_TIMEOUT_MS. Use COLLECT_FETCH_TIMEOUT_MS for collect loop.
 */
export async function getSignatures(
  relay: string,
  hash: string,
  timeoutMs?: number,
): Promise<MsgSignature[]> {
  const ms = timeoutMs ?? RELAY_FETCH_TIMEOUT_MS;
  const response = await fetch(`${relay}/signatures/${hash}`, {
    signal: AbortSignal.timeout(ms),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /signatures/${hash} failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as MsgSignature[];
}

/**
 * GET decryption keys for a message hash.
 * Throws on fetch failure or non-ok response.
 */
export async function getKeys(relay: string, hash: string): Promise<MsgCle[]> {
  const response = await fetch(`${relay}/keys/${hash}`, {
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /keys/${hash} failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as MsgCle[];
}

/**
 * GET decryption keys in time window (received_at).
 * Used for scan-first flow: fetch keys, then fetch messages by hash.
 * Throws on fetch failure or non-ok response.
 */
export async function getKeysInWindow(
  relay: string,
  start: number,
  end: number,
): Promise<MsgCle[]> {
  const params = new URLSearchParams({
    start: start.toString(),
    end: end.toString(),
  });
  const response = await fetch(`${relay}/keys?${params.toString()}`, {
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /keys?start=&end= failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as MsgCle[];
}

/**
 * GET a single encrypted message by hash.
 * Throws on fetch failure or non-ok response. 404 throws.
 */
export async function getMessageByHash(
  relay: string,
  hash: string,
): Promise<MsgChiffre> {
  const response = await fetch(`${relay}/messages/${hash}`, {
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /messages/${hash} failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as MsgChiffre;
}

/**
 * POST encrypted message to relay.
 * Throws on fetch failure or non-ok response.
 */
export async function postMessageChiffre(relay: string, message: MsgChiffre): Promise<void> {
  const response = await fetch(`${relay}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(message),
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay POST /messages failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
}

/**
 * POST signature to relay.
 * Throws on fetch failure or non-ok response.
 */
export async function postSignature(relay: string, signature: MsgSignature): Promise<void> {
  const response = await fetch(`${relay}/signatures`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(signature),
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay POST /signatures failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
}

/**
 * POST decryption key to relay.
 * Throws on fetch failure or non-ok response.
 */
export async function postKey(relay: string, key: MsgCle): Promise<void> {
  const response = await fetch(`${relay}/keys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(key),
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay POST /keys failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
}

/**
 * GET Bloom filter (JSON) of seen message hashes from relay.
 * Optional: clients can use it to avoid requesting messages for hashes already known.
 * Throws on fetch failure or non-ok response.
 */
export async function getBloom(relay: string): Promise<unknown> {
  const response = await fetch(`${relay}/bloom`, {
    signal: AbortSignal.timeout(RELAY_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new Error(
      `Relay GET /bloom failed: ${response.status} ${response.statusText} (${relay})`,
    );
  }
  return (await response.json()) as unknown;
}
